import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// frontend/src/components/SkillsBoard/SkillsBoard.tsx
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@weave-mui/material';
import { useSkills, useBatchDeleteSkills } from '../../api/hooks';
import { getErrorDetail } from '../../utils/errorDetail';
import { useBoardSelection } from '../../hooks/useBoardSelection';
import BoardHeader from '../BoardHeader/BoardHeader';
import DeleteConfirmDialog from '../BoardHeader/DeleteConfirmDialog';
import SkillCard from './SkillCard';
import SkillFormModal from './SkillFormModal';
import SkillSidebar from './SkillSidebar';
const COLUMNS = [
    { type: 'task-from-slack-app', label: 'Slack tasks' },
    { type: 'task-from-slack-message', label: 'Slack auto-triage' },
    { type: 'task-from-github-app', label: 'GitHub tasks' },
    { type: 'normal-task', label: 'Normal tasks' },
];
export default function SkillsBoard() {
    const navigate = useNavigate();
    const { skillId } = useParams();
    const { data, isLoading, error, refetch, isRefetching, } = useSkills();
    const [createOpen, setCreateOpen] = useState(false);
    const { mutateAsync: batchDeleteSkills } = useBatchDeleteSkills();
    const { selectedIds, setSelectedIds, handleClick } = useBoardSelection();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    if (isLoading)
        return _jsx(CircularProgress, { sx: { m: 4 } });
    if (error) {
        return (_jsxs(Typography, { color: "error", sx: { m: 4 }, children: ["Failed to load skills: ", getErrorDetail(error)] }));
    }
    const skills = data?.results ?? [];
    const byType = (type) => skills.filter((s) => s.type === type);
    const selectedSkill = skillId ? skills.find((s) => s.skillId === skillId) : undefined;
    async function handleConfirmDelete() {
        setDeleting(true);
        setDeleteError(null);
        try {
            const outcome = await batchDeleteSkills(Array.from(selectedIds));
            if (outcome.errors.length > 0) {
                setSelectedIds(new Set(outcome.errors.map((e) => e.id)));
                setDeleteError(`Failed to delete ${outcome.errors.length} of ${outcome.errors.length + outcome.results.length} skill(s). They remain selected — try again.`);
            }
            else {
                setSelectedIds(new Set());
                setDeleteDialogOpen(false);
            }
        }
        catch (e) {
            setDeleteError(`Failed to delete skills: ${getErrorDetail(e)}`);
        }
        finally {
            setDeleting(false);
        }
    }
    return (_jsxs(Box, { children: [_jsx(BoardHeader, { title: "Skills", actionLabel: "New Skill", onAction: () => setCreateOpen(true), onRefresh: () => refetch(), refreshing: isRefetching, selectedCount: selectedIds.size, onDelete: () => { setDeleteError(null); setDeleteDialogOpen(true); } }), skills.length === 0 ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', mt: 8 }, children: _jsx(Typography, { color: "text.secondary", children: "No skills yet. Create one to get started." }) })) : (_jsxs(Box, { sx: { display: 'flex', gap: 2, alignItems: 'flex-start' }, children: [_jsx(Box, { sx: { flex: 1, minWidth: 0, display: 'flex', gap: 2 }, children: COLUMNS.map(({ type, label }) => (_jsxs(Box, { sx: { flex: 1, minWidth: 0, bgcolor: '#f4f5f7', borderRadius: 2, p: 1, minHeight: 400 }, children: [_jsxs(Typography, { variant: "subtitle2", sx: { mb: 1, fontWeight: 700 }, children: [label, " (", byType(type).length, ")"] }), byType(type).map((skill) => (_jsx(SkillCard, { skill: skill, selected: skillId === skill.skillId, multiSelected: selectedIds.has(skill.skillId), onClick: (s, e) => handleClick(s.skillId, type, byType(type).map((x) => x.skillId), e, () => navigate(`/skills/${s.skillId}`)) }, skill.skillId)))] }, type))) }), selectedSkill && (
                    // Keyed by skillId so switching to a different skill remounts the sidebar —
                    // its editable fields are seeded once, at mount, from the `skill` prop.
                    _jsx(SkillSidebar, { skill: selectedSkill, onClose: () => navigate('/skills') }, selectedSkill.skillId))] })), _jsx(SkillFormModal, { open: createOpen, onClose: () => setCreateOpen(false) }), _jsx(DeleteConfirmDialog, { open: deleteDialogOpen, count: selectedIds.size, itemLabel: "skill", onCancel: () => { setDeleteDialogOpen(false); setDeleteError(null); }, onConfirm: handleConfirmDelete, deleting: deleting, error: deleteError })] }));
}
